import React, { useState, useEffect, useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useInView,
} from 'framer-motion';
import {
  PenTool, Megaphone, Code2, Layout, Rocket, Palette,
  Film, Music, GraduationCap, Building2, ShoppingBag, ShieldCheck,
} from 'lucide-react';
import { NavBar } from './ui/TubelightNavbar.jsx';
import { CinematicBackground } from './ui/CinematicBackground';
import { Logo } from './Logo';
import { Footer } from './ui/Footer';
import LoginModal from './auth/LoginModal';

/* ═══════════════════════════════════════════════════════════════
   SOLUTIONS - Editorial magazine edition
   ───────────────────────────────────────────────────────────────
   Design principles:
     • Magazine-grade typography (Fraunces + JetBrains Mono)
     • Real breathing room - nothing feels cramped
     • Asymmetric bento grid - spotlight cards mix with standard
     • Restrained motion - confident, not showy
     • Violet accents, never overwhelming
   ═══════════════════════════════════════════════════════════════ */

const SP = {
  bg:          'transparent',
  canvas:      'rgba(0,0,0,0.4)',
  canvasCool:  'rgba(0,0,0,0.6)',
  ink:         '#FFFFFF',
  inkSoft:     'rgba(255,255,255,0.9)',
  muted:       'rgba(255,255,255,0.7)',
  subtle:      'rgba(255,255,255,0.4)',
  faint:       'rgba(255,255,255,0.2)',
  line:        'rgba(255,255,255,0.1)',
  lineSoft:    'rgba(255,255,255,0.05)',
  accentA:     '#7C3AED', // violet-600
  accentB:     '#A78BFA', // violet-400
  accentC:     '#C4B5FD', // violet-300
  accentGrad:  'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
  accentTint:  'rgba(124,58,237,0.1)',
  accentTintB: 'rgba(124,58,237,0.2)',
  accentDeep:  '#C4B5FD',
  accentInk:   '#FFFFFF',
};
const SERIF = '"Nasalization", "Instrument Serif", Georgia, serif';
const MONO  = '"JetBrains Mono", ui-monospace, Menlo, monospace';

/**
 * useBreakpoint - small hook returning { isMobile, isTablet, isDesktop }.
 * Drives responsive inline styles across the page.
 *    mobile  : < 640px
 *    tablet  : 640 - 1023
 *    desktop : ≥ 1024
 */
function useBreakpoint() {
  const read = () => {
    if (typeof window === 'undefined') return { isMobile: false, isTablet: false, isDesktop: true };
    const w = window.innerWidth;
    return { isMobile: w < 640, isTablet: w >= 640 && w < 1024, isDesktop: w >= 1024 };
  };
  const [bp, setBp] = useState(read);
  useEffect(() => {
    const onResize = () => setBp(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return bp;
}

/* Layout = 'span2' means the card takes two columns (spotlight). */
const SOLUTIONS = [
  { num: '01', tag: 'CREATORS',    layout: 'span2', title: 'Content creators',          pitch: 'Across every platform.',                          body: 'Draft threads, script reels, and publish-ready visuals from a single prompt. Voice and brand stay consistent across every channel.', tools: ['CHAT','IMAGE','VIDEO'], icon: PenTool,       hue: '#7C3AED' },
  { num: '02', tag: 'MARKETING',   layout: 'span1', title: 'Marketing teams',           pitch: null,                                              body: 'Campaign copy, display ads, and A/B variants - tested against brand tokens before a dollar touches media.',                           tools: ['CHAT','IMAGE'],         icon: Megaphone,     hue: '#8B5CF6' },
  { num: '03', tag: 'DEVELOPERS',  layout: 'span1', title: 'Indie developers',          pitch: null,                                              body: 'Prototype UIs, generate boilerplate, and debug across 400+ models. Every task routes to the model that answers it best.',             tools: ['CHAT','CODE'],          icon: Code2,         hue: '#6366F1' },
  { num: '04', tag: 'NO-CODE',     layout: 'span1', title: 'No-code builders',          pitch: null,                                              body: 'Describe the product. DAGGPT composes layout, copy, and imagery - then ships a deployable site end-to-end.',                          tools: ['WEBSITE','IMAGE'],      icon: Layout,        hue: '#A855F7' },
  { num: '05', tag: 'FOUNDERS',    layout: 'span2', title: 'Founders & early startups', pitch: 'Ship faster than you fundraise.',                 body: 'Investor decks, landing pages, and brand systems overnight. Turn an idea into a live presence between morning coffee and first meeting.', tools: ['CHAT','IMAGE','WEBSITE'], icon: Rocket,      hue: '#9333EA' },
  { num: '06', tag: 'DESIGNERS',   layout: 'span1', title: 'Designers & art directors', pitch: null,                                              body: 'Mood boards, concept frames, and editorial imagery at agency fidelity. Imagen, FLUX, and Ideogram in one canvas.',                    tools: ['IMAGE'],                icon: Palette,       hue: '#7E22CE' },
  { num: '07', tag: 'FILM',        layout: 'span1', title: 'Filmmakers & editors',      pitch: null,                                              body: 'Block shots on Veo, generate B-roll on Runway, and assemble storyboards in seconds. Blank page to screening in an afternoon.',         tools: ['VIDEO','IMAGE'],        icon: Film,          hue: '#6D28D9' },
  { num: '08', tag: 'MUSIC',       layout: 'span1', title: 'Musicians & producers',     pitch: null,                                              body: 'Compose demos, sketch vocal ideas, and stem out full tracks with Lyria, Suno, and Udio. A studio that listens in plain English.',     tools: ['MUSIC'],                icon: Music,         hue: '#5B21B6' },
  { num: '09', tag: 'EDUCATION',   layout: 'span2', title: 'Educators & researchers',   pitch: 'Long-context, citation-grade.',                   body: 'Synthesize papers, compare frontier models side-by-side, and tailor lesson material to the room. The longest context windows on the market, in one thread.', tools: ['CHAT'], icon: GraduationCap, hue: '#7C3AED' },
  { num: '10', tag: 'AGENCIES',    layout: 'span1', title: 'Agencies & studios',        pitch: null,                                              body: 'Client variations, campaign rounds, and pitch decks in parallel. Shared workspaces and per-client billing come standard.',             tools: ['CHAT','IMAGE','VIDEO'], icon: Building2,    hue: '#8B5CF6' },
  { num: '11', tag: 'COMMERCE',    layout: 'span1', title: 'E-commerce brands',         pitch: null,                                              body: 'Turn a single product photo into lifestyle shots, ad creatives, and seasonal campaigns. Catalog-scale, reviewed before it ships.',     tools: ['IMAGE','VIDEO'],        icon: ShoppingBag,   hue: '#A855F7' },
  { num: '12', tag: 'ENTERPRISE',  layout: 'span1', title: 'Enterprise teams',          pitch: null,                                              body: 'SSO, audit logs, department-level billing, and private model routing. Everything your security team asks for - out of the box.',      tools: ['SSO','AUDIT','BILLING'], icon: ShieldCheck, hue: '#6366F1' },
];

/* Map solution tools to the correct workspace route */
function getWorkspaceRoute(tools) {
  if (tools.includes('MUSIC')) return '/music-generation';
  if (tools.includes('VIDEO')) return '/video-generation';
  if (tools.includes('IMAGE')) return '/image-generation';
  if (tools.includes('WEBSITE')) return '/website-builder';
  return '/chat'; // Default for CHAT, CODE, and everything else
}

/* ═══════════════════════════════════════════════════════════════
   Page
   ═══════════════════════════════════════════════════════════════ */
export function SolutionsPage() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const navItems = [
    { name: 'Home',       url: '/',          icon: 'home' },
    { name: 'Solutions',  url: '/solutions' },
    { name: 'Models',     url: '/models'    },
    { name: 'Enterprise', url: '/enterprise'},
  ];

  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 80, damping: 28, mass: 0.6 });

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        color: SP.ink,
        background: SP.bg,
        overflowX: 'hidden',
      }}
    >
      {/* Cinematic Background */}
      <CinematicBackground />

      {/* Chrome */}
      <div className="fixed top-4 left-4 md:top-6 md:left-8 z-50"><Logo /></div>
      <NavBar items={navItems} onSignInClick={() => setShowLoginModal(true)} />

      <ScrollProgressBar progress={smoothProgress} />

      {/* Content */}
      <HeroSection />
      <IndexBanner />
      <EditorialSpread />
      <ManifestoSection />
      <CtaSection onSignInClick={() => setShowLoginModal(true)} />

      <Footer />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────
   Scroll progress rail (thin violet line at top)
   ─────────────────────────────────────────────────────────────── */
function ScrollProgressBar({ progress }) {
  const scaleX = useTransform(progress, [0, 1], [0, 1]);
  return (
    <motion.div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: '2px',
        background: SP.accentGrad,
        transformOrigin: 'left',
        scaleX,
        zIndex: 200,
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════
   HERO - confident, spacious, restrained.
   ═══════════════════════════════════════════════════════════════ */
function HeroSection() {
  const { isMobile, isTablet } = useBreakpoint();
  const words = ['Built', 'for', 'the', 'people', 'who'];
  return (
    <section style={{
      position: 'relative',
      textAlign: 'center',
      maxWidth: '1240px',
      margin: '0 auto',
      padding: isMobile ? '120px 20px 72px' : isTablet ? '150px 32px 96px' : '180px 40px 120px',
      zIndex: 1,
    }}>
      {/* Headline - word-by-word build
          line-height 1.05 + bottom padding prevents italic descenders
          (y / p in "actually ship") from being clipped by the line box. */}
      <h1 style={{
        fontFamily: SERIF,
        fontSize: 'clamp(52px, 8.4vw, 124px)',
        lineHeight: 1.05,
        letterSpacing: '-0.04em',
        color: SP.ink,
        fontWeight: 400,
        margin: '0 auto 44px',
        maxWidth: '1120px',
        paddingBottom: '0.12em',
      }}>
        {words.map((w, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 + i * 0.08, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            style={{
              display: 'inline-block',
              marginRight: '0.22em',
              paddingBottom: '0.18em',
            }}
          >
            {w}
          </motion.span>
        ))}
        <motion.em
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 + words.length * 0.08, duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
          style={{
            fontStyle: 'italic',
            background: SP.accentGrad,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
            padding: '0 0.06em 0.18em',
            marginRight: '0.1em',
          }}
        >
          actually ship
        </motion.em>
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.18 + (words.length + 1) * 0.08, duration: 0.7 }}
        >
          the work.
        </motion.span>
      </h1>

      {/* Subcopy */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.7 }}
        style={{
          fontSize: '19px',
          lineHeight: 1.6,
          color: SP.muted,
          maxWidth: '640px',
          margin: '0 auto 48px',
          fontWeight: 400,
        }}
      >
        DAGGPT isn't a single model or a single canvas. It's twelve opinionated
        workflows - routed through the best model for every step.
      </motion.p>

      {/* CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        style={{ display: 'inline-flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}
      >
        <PrimaryCTA href="/workspace">Start creating</PrimaryCTA>
        <GhostCTA href="#lineup" trailing="→">See the lineup</GhostCTA>
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   INDEX BANNER - editorial marquee
   A single drifting line of all twelve chapters, magazine-style.
   Serif names, mono numerals, violet lozenge between entries.
   No cells, no dividers - just type on the move.
   ═══════════════════════════════════════════════════════════════ */
function IndexBanner() {
  const { isMobile, isTablet } = useBreakpoint();
  // The names we want to see - short, scannable, italic serif.
  const entries = SOLUTIONS.map(s => ({
    name: s.title.replace(' & early startups', '').replace(' & art directors', '').replace(' & editors', '').replace(' & producers', '').replace(' & researchers', '').replace(' & studios', ''),
    tag: s.tag,
  }));
  // Double the list so the translate loop appears seamless.
  const row = [...entries, ...entries];

  return (
    <section style={{
      position: 'relative',
      zIndex: 1,
      margin: '0 0 120px',
      padding: 0,
      borderBottom: `1px solid ${SP.line}`,
      overflow: 'hidden',
      background: 'rgba(255, 255, 255, 0.02)',
      backdropFilter: 'blur(8px)',
    }}>
      <motion.div
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: isMobile ? 55 : 80, repeat: Infinity, ease: 'linear' }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '28px' : '56px',
          padding: isMobile ? '22px 0' : '38px 0',
          whiteSpace: 'nowrap',
          width: 'max-content',
        }}
      >
        {row.map((e, i) => (
          <div key={i} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: isMobile ? '28px' : '56px',
          }}>
            <span style={{
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontSize: isMobile ? '20px' : isTablet ? '26px' : '32px',
              color: SP.ink,
              fontWeight: 400,
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}>
              {e.name}
            </span>
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: isMobile ? '5px' : '8px',
                height: isMobile ? '5px' : '8px',
                borderRadius: '50%',
                background: SP.accentGrad,
                flexShrink: 0,
              }}
            />
          </div>
        ))}
      </motion.div>

      {/* Edge fades */}
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: isMobile ? '60px' : '180px',
        background: 'linear-gradient(90deg, #050505 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: isMobile ? '60px' : '180px',
        background: 'linear-gradient(-90deg, #050505 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   EDITORIAL SPREAD
   The main event. Asymmetric magazine grid - three columns with
   spotlight cards spanning two columns. Every row feels different.
   No scroll pinning. Cards scroll naturally at reading speed.
   ═══════════════════════════════════════════════════════════════ */
function EditorialSpread() {
  const { isMobile, isTablet } = useBreakpoint();
  return (
    <section
      id="lineup"
      style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: '1340px',
        margin: '0 auto',
        padding: isMobile ? '24px 20px 80px' : isTablet ? '32px 32px 100px' : '40px 40px 140px',
      }}
    >
      {/* Section header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: isMobile ? '20px' : '32px',
        marginBottom: isMobile ? '48px' : '80px',
        paddingBottom: isMobile ? '18px' : '24px',
        borderBottom: `1px solid ${SP.line}`,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ marginBottom: '18px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: SP.accentGrad }} />
            <span style={{
              fontFamily: MONO, fontSize: '10px', letterSpacing: '0.28em',
              color: SP.ink, fontWeight: 600, textTransform: 'uppercase',
            }}>
              The lineup · 
            </span>
          </div>
          <h2 style={{
            fontFamily: SERIF,
            fontSize: 'clamp(40px, 5.2vw, 72px)',
            lineHeight: 1.0,
            letterSpacing: '-0.035em',
            color: SP.ink,
            fontWeight: 400,
            margin: 0,
            maxWidth: '820px',
          }}>
            Twelve workflows,{' '}
            <em style={{
              fontStyle: 'italic',
              background: SP.accentGrad,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              paddingRight: '0.12em',
              paddingBottom: '0.08em',
            }}>
              each purpose-built
            </em>
            .
          </h2>
        </div>
        <p style={{
          fontFamily: SERIF,
          fontSize: '17px',
          lineHeight: 1.55,
          color: SP.muted,
          margin: 0,
          maxWidth: '380px',
          fontStyle: 'italic',
        }}>
          Three spotlight chapters, nine working companions. Scan the ones that
          describe your week - the rest will be there when you need them.
        </p>
      </div>

      {/* The grid - responsive columns.
          Mobile: 1-col stack. Tablet: 2-col. Desktop: 3-col with spotlights spanning 2.
          `dense` lets the browser backfill single-col cards into any gap
          left by a spotlight that wrapped to the next row - no holes. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gridAutoFlow: 'dense',
        gap: isMobile ? '16px' : '24px',
      }}>
        {SOLUTIONS.map((sol, i) => (
          <SolutionCard key={sol.num} sol={sol} index={i} />
        ))}
      </div>
    </section>
  );
}

/* Solution card - two treatments and two sizes.
   • span2 + pitch → spotlight card (dark editorial, giant numeral, pitch line)
   • span1         → standard card (clean white, tasteful) */
function SolutionCard({ sol, index }) {
  const { num, tag, layout, title, pitch, body, tools, icon: Icon, hue } = sol;
  const { isMobile, isTablet } = useBreakpoint();
  // Spotlights only span 2 cols on desktop; on mobile/tablet every card is full-width.
  const isSpotlight = layout === 'span2' && !isMobile && !isTablet;

  const cardRef = useRef(null);
  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const spX = useSpring(rotX, { stiffness: 260, damping: 22 });
  const spY = useSpring(rotY, { stiffness: 260, damping: 22 });

  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    rotY.set((px - 0.5) * (isSpotlight ? 4 : 6));
    rotX.set(-(py - 0.5) * (isSpotlight ? 4 : 6));
  };
  const onLeave = () => { rotX.set(0); rotY.set(0); };

  return (
    <motion.article
      id={`sol-${num}`}
      ref={cardRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{
        gridColumn: isSpotlight ? 'span 2' : 'span 1',
        position: 'relative',
        minHeight: isMobile ? (layout === 'span2' ? '360px' : '300px') : (isSpotlight ? '440px' : '360px'),
        padding: isMobile ? '26px 24px' : isSpotlight ? '44px 44px' : '32px 32px',
        borderRadius: isMobile ? '22px' : '28px',
        background: isSpotlight
          ? 'linear-gradient(155deg, rgba(10,10,10,0.6) 0%, rgba(26,16,51,0.6) 55%, rgba(10,10,26,0.6) 100%)'
          : 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${isSpotlight ? 'rgba(167,139,250,0.22)' : SP.line}`,
        boxShadow: isSpotlight
          ? `0 40px 80px -28px ${hue}50, inset 0 1px 0 rgba(255,255,255,0.05)`
          : '0 4px 24px -1px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
        transformPerspective: 1200,
        rotateX: spX,
        rotateY: spY,
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Background decoration */}
      {isSpotlight ? (
        <>
          {/* Hue glow */}
          <div style={{
            position: 'absolute',
            top: '-30%', right: '-10%',
            width: '620px', height: '620px',
            background: `radial-gradient(circle at 60% 40%, ${hue}55 0%, transparent 60%)`,
            filter: 'blur(30px)',
            pointerEvents: 'none',
          }} />
          {/* Giant ghost numeral */}
          <div style={{
            position: 'absolute',
            right: '-6%', bottom: '-24%',
            fontFamily: SERIF,
            fontSize: '480px',
            lineHeight: 1,
            fontWeight: 400,
            letterSpacing: '-0.05em',
            color: 'rgba(167,139,250,0.11)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}>
            {num}
          </div>
        </>
      ) : (
        <div style={{
          position: 'absolute',
          top: '-40%', right: '-40%',
          width: '240px', height: '240px',
          background: `radial-gradient(circle, ${SP.accentTint} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
      )}

      {/* Top row - tag pill only (numerals removed for a cleaner face) */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex',
        alignItems: 'center',
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '5px 11px', borderRadius: '999px',
          background: isSpotlight ? 'rgba(167,139,250,0.18)' : SP.accentTint,
          border: isSpotlight ? '1px solid rgba(167,139,250,0.20)' : `1px solid ${SP.accentTintB}`,
          fontFamily: MONO, fontSize: '9.5px', letterSpacing: '0.2em',
          color: isSpotlight ? '#C4B5FD' : SP.accentDeep, fontWeight: 700,
        }}>
          {tag}
        </span>
      </div>

      {/* Middle - icon + title + optional pitch + body */}
      <div style={{ position: 'relative', zIndex: 2, margin: isMobile ? '20px 0' : isSpotlight ? '40px 0' : '24px 0' }}>
        <div style={{
          width: isMobile ? '44px' : isSpotlight ? '60px' : '48px',
          height: isMobile ? '44px' : isSpotlight ? '60px' : '48px',
          borderRadius: isMobile ? '12px' : isSpotlight ? '18px' : '14px',
          background: isSpotlight ? 'rgba(167,139,250,0.16)' : SP.accentTint,
          border: isSpotlight ? '1px solid rgba(167,139,250,0.28)' : `1px solid ${SP.accentTintB}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: isMobile ? '18px' : '26px',
          boxShadow: isSpotlight ? `0 10px 20px -6px ${hue}55` : 'none',
        }}>
          <Icon size={isMobile ? 20 : isSpotlight ? 28 : 22} color={isSpotlight ? '#C4B5FD' : SP.accentA} strokeWidth={1.6} />
        </div>

        <h3 style={{
          fontFamily: SERIF,
          fontSize: isMobile ? '24px' : isSpotlight ? '44px' : '26px',
          lineHeight: 1.02,
          letterSpacing: '-0.028em',
          color: isSpotlight ? '#FFFFFF' : SP.ink,
          fontWeight: 400,
          margin: '0 0 14px',
          maxWidth: isSpotlight ? '520px' : '100%',
        }}>
          {title}
        </h3>

        {isSpotlight && pitch && (
          <p style={{
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontSize: '22px',
            lineHeight: 1.3,
            background: 'linear-gradient(135deg, #C4B5FD 0%, #E9D5FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 400,
            margin: '0 0 22px',
            letterSpacing: '-0.015em',
          }}>
            {pitch}
          </p>
        )}

        <p style={{
          fontSize: isSpotlight ? '16.5px' : '14.5px',
          lineHeight: 1.6,
          color: isSpotlight ? 'rgba(255,255,255,0.72)' : SP.muted,
          margin: 0,
          fontWeight: 400,
          maxWidth: isSpotlight ? '560px' : '100%',
        }}>
          {body}
        </p>
      </div>

      {/* Bottom - tools + chevron */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        paddingTop: '20px',
        borderTop: `1px solid ${isSpotlight ? 'rgba(255,255,255,0.09)' : SP.line}`,
      }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {tools.map(t => (
            <span key={t} style={{
              padding: '5px 10px', borderRadius: '6px',
              background: isSpotlight ? 'rgba(255,255,255,0.06)' : SP.canvas,
              fontFamily: MONO, fontSize: '9.5px', letterSpacing: '0.18em',
              color: isSpotlight ? 'rgba(255,255,255,0.75)' : SP.muted, fontWeight: 600,
            }}>
              {t}
            </span>
          ))}
        </div>
        <a
          href={getWorkspaceRoute(tools)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: isSpotlight ? '#C4B5FD' : SP.accentA,
            fontFamily: MONO, fontSize: '10px', letterSpacing: '0.2em',
            fontWeight: 700, textTransform: 'uppercase', textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Open
          <span style={{ fontSize: '13px', transform: 'translateY(-1px)' }}>→</span>
        </a>
      </div>
    </motion.article>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MANIFESTO
   Quiet oversized typographic statement. No tricks - just type.
   ═══════════════════════════════════════════════════════════════ */
function ManifestoSection() {
  const { isMobile, isTablet } = useBreakpoint();
  return (
    <section style={{
      position: 'relative',
      zIndex: 1,
      maxWidth: '1240px',
      margin: '0 auto',
      padding: isMobile ? '60px 20px 100px' : isTablet ? '70px 32px 130px' : '80px 40px 160px',
      textAlign: 'center',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <div style={{ marginBottom: '32px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: SP.accentGrad }} />
          <span style={{
            fontFamily: MONO, fontSize: '10px', letterSpacing: '0.28em',
            color: SP.ink, fontWeight: 600, textTransform: 'uppercase',
          }}>
            The north star
          </span>
        </div>

        <h2 style={{
          fontFamily: SERIF,
          fontSize: 'clamp(40px, 6.4vw, 92px)',
          lineHeight: 1.02,
          letterSpacing: '-0.035em',
          color: SP.ink,
          fontWeight: 400,
          margin: '0 auto',
          maxWidth: '1020px',
        }}>
          The{' '}
          <em style={{
            fontStyle: 'italic',
            background: SP.accentGrad,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
            paddingRight: '0.12em',
            paddingBottom: '0.08em',
          }}>
            right model
          </em>
          , for the{' '}
          <em style={{
            fontStyle: 'italic',
            background: SP.accentGrad,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
            paddingRight: '0.12em',
            paddingBottom: '0.08em',
          }}>
            right job
          </em>
          .
        </h2>

        <p style={{
          fontFamily: SERIF,
          fontStyle: 'italic',
          fontSize: '20px',
          lineHeight: 1.55,
          color: SP.muted,
          margin: '36px auto 0',
          maxWidth: '620px',
        }}>
          Not a pile of models you have to manage. A platform that knows which
          one to reach for - and gets out of the way.
        </p>
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CTA - glass panel with ambient orb. Calm, final.
   ═══════════════════════════════════════════════════════════════ */
function CtaSection({ onSignInClick }) {
  const { isMobile, isTablet } = useBreakpoint();
  return (
    <section style={{
      position: 'relative',
      zIndex: 1,
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px 100px' : isTablet ? '10px 32px 140px' : '20px 40px 180px',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 24px' : isTablet ? '72px 48px' : '88px 64px',
          borderRadius: isMobile ? '24px' : '36px',
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(8px)',
          border: `1px solid ${SP.accentTintB}`,
          overflow: 'hidden',
          textAlign: 'center',
          boxShadow: '0 48px 120px -40px rgba(124,58,237,0.28)',
        }}
      >
        {/* Ambient orbs */}
        <div style={{
          position: 'absolute', top: '-50%', left: '-20%',
          width: '580px', height: '580px',
          background: 'radial-gradient(circle, rgba(167,139,250,0.38) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-55%', right: '-20%',
          width: '620px', height: '620px',
          background: 'radial-gradient(circle, rgba(196,181,253,0.32) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '26px',
        }}>
          <motion.span
            animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            style={{ width: '6px', height: '6px', borderRadius: '50%', background: SP.accentGrad }}
          />
          <span style={{
            fontFamily: MONO, fontSize: '10px', letterSpacing: '0.28em',
            color: SP.accentDeep, fontWeight: 600, textTransform: 'uppercase',
          }}>
            Ready when you are
          </span>
        </div>

        <h2 style={{
          position: 'relative',
          fontFamily: SERIF,
          fontSize: 'clamp(42px, 6vw, 80px)',
          lineHeight: 1.0,
          letterSpacing: '-0.035em',
          color: SP.ink,
          fontWeight: 400,
          margin: '0 auto 22px',
          maxWidth: '840px',
        }}>
          Pick a workflow. Ship something{' '}
          <em style={{
            fontStyle: 'italic',
            background: SP.accentGrad,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
            paddingRight: '0.12em',
            paddingBottom: '0.08em',
          }}>
            tonight
          </em>
          .
        </h2>

        <p style={{
          position: 'relative',
          fontSize: '18px',
          lineHeight: 1.6,
          color: SP.muted,
          maxWidth: '580px',
          margin: '0 auto 44px',
        }}>
          Every solution lives inside the same workspace. No new tools to learn,
          no subscriptions to stack - just the right model, for the right job.
        </p>

        <div style={{
          position: 'relative',
          display: 'inline-flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center',
        }}>
          <PrimaryCTA href="/chat">Open the workspace</PrimaryCTA>
          <GhostCTA onClick={onSignInClick} trailing="→">Sign in to save work</GhostCTA>
        </div>

        <div style={{
          position: 'relative',
          display: 'flex', justifyContent: 'center',
          gap: isMobile ? '14px' : '32px',
          marginTop: isMobile ? '28px' : '44px',
          flexWrap: 'wrap',
          rowGap: '12px',
        }}>
          {['Pay as you go', 'Switch workflows anytime', 'One unified bill'].map(t => (
            <div key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: SP.accentA, fontFamily: MONO, fontSize: '11px', fontWeight: 700 }}>✓</span>
              <span style={{
                fontFamily: MONO, fontSize: '10px', letterSpacing: '0.18em',
                color: SP.muted, fontWeight: 600, textTransform: 'uppercase',
              }}>
                {t}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────
   Shared CTA primitives
   ─────────────────────────────────────────────────────────────── */
function PrimaryCTA({ children, href, onClick }) {
  const style = {
    display: 'inline-flex', alignItems: 'center',
    padding: '16px 34px', borderRadius: '999px',
    background: SP.accentGrad, color: '#FFFFFF',
    fontFamily: MONO, fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.24em', textTransform: 'uppercase',
    textDecoration: 'none', cursor: 'pointer', border: 'none',
    boxShadow: '0 14px 32px -14px rgba(124,58,237,0.55)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  };
  const onEnter = e => {
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = '0 20px 40px -16px rgba(124,58,237,0.7)';
  };
  const onLeave = e => {
    e.currentTarget.style.transform = 'none';
    e.currentTarget.style.boxShadow = '0 14px 32px -14px rgba(124,58,237,0.55)';
  };
  if (href) return (
    <a href={href} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
    </a>
  );
  return (
    <button onClick={onClick} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
    </button>
  );
}

function GhostCTA({ children, trailing, href, onClick }) {
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: '12px',
    padding: '16px 32px', borderRadius: '999px',
    background: 'rgba(255,255,255,0.03)', color: '#FFFFFF',
    border: `1px solid rgba(255,255,255,0.1)`,
    fontFamily: MONO, fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.24em', textTransform: 'uppercase',
    textDecoration: 'none', cursor: 'pointer',
    transition: 'background 0.2s, border 0.2s, transform 0.2s',
  };
  const onEnter = e => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
    e.currentTarget.style.borderColor = SP.accentTintB;
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const onLeave = e => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
    e.currentTarget.style.transform = 'none';
  };
  const content = (
    <>
      <span>{children}</span>
      {trailing && (
        <span style={{ fontSize: '14px', letterSpacing: 0, transform: 'translateY(-1px)' }}>{trailing}</span>
      )}
    </>
  );
  if (href) return (
    <a href={href} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{content}</a>
  );
  return (
    <button onClick={onClick} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{content}</button>
  );
}

export default SolutionsPage;
